import { useState, useEffect } from "react";
import { fetchTypeList, fetchPermissionList, updatePermissionList } from "../model/PermissionModel";
import { useToast } from "../../../basecomp/components/provider/ToastProvider";

const PermissionVM = () => {
    const { handleToastOpen } = useToast();
    
    // Permission states
    const [permissionType, setPermissionType] = useState('');
    const [selectedTarget, setSelectedTarget] = useState('');
    const [checksList, setChecksList] = useState(new Set());
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // Dynamic data states
    const [dataListByType, setDataListByType] = useState([]);
    const [dataDisplay, setDataDisplay] = useState({});
    const [dataGroup, setDataGroup] = useState({});

    // Transform flat permission data to hierarchical structure
    const transformPermissionsData = (flatData) => {
        const hierarchicalData = {};
        
        Object.entries(flatData).forEach(([id, description]) => {
            // Extract the permission key (everything before the " - " if it exists)
            const permissionKey = description.split(' - ')[0];
            
            if (permissionKey.startsWith('tool_')) {
                // This is a tool (main header)
                const toolName = permissionKey.replace('tool_', '').toUpperCase();
                
                if (!hierarchicalData[toolName]) {
                    hierarchicalData[toolName] = {};
                }
                
                // Store the tool ID for reference
                hierarchicalData[toolName]._toolId = id;
                
            } else if (permissionKey.startsWith('menu_')) {
                // This is a menu (submenu)
                const parts = permissionKey.replace('menu_', '').split('_');
                const toolName = parts[0].toUpperCase();
                const menuName = parts.slice(1).join('_').replace(/_/g, ' ').toUpperCase();
                
                if (!hierarchicalData[toolName]) {
                    hierarchicalData[toolName] = {};
                }
                
                if (!hierarchicalData[toolName][menuName]) {
                    hierarchicalData[toolName][menuName] = [];
                }
                
                // Store the menu ID for reference
                hierarchicalData[toolName][menuName]._menuId = id;
                
            } else if (permissionKey.startsWith('feature_')) {
                // This is a feature
                const parts = permissionKey.replace('feature_', '').split('_');
                const toolName = parts[0].toUpperCase();
                const featureName = parts.slice(1).join('_').replace(/_/g, ' ');
                
                if (!hierarchicalData[toolName]) {
                    hierarchicalData[toolName] = {};
                }
                
                // Find the appropriate menu for this feature or create a default one
                let targetMenu = 'FEATURES';
                
                // Look for existing menus in this tool to place the feature
                const existingMenus = Object.keys(hierarchicalData[toolName]).filter(key => !key.startsWith('_'));
                if (existingMenus.length > 0) {
                    // For now, put features in the last menu or create a FEATURES menu
                    const hasGenericMenu = existingMenus.find(menu => 
                        menu.includes('FEATURE') || menu.includes('ACTION') || menu.includes('OTHER')
                    );
                    
                    if (hasGenericMenu) {
                        targetMenu = hasGenericMenu;
                    } else if (existingMenus.length === 1) {
                        targetMenu = existingMenus[0];
                    }
                }
                
                if (!hierarchicalData[toolName][targetMenu]) {
                    hierarchicalData[toolName][targetMenu] = [];
                }
                
                if (Array.isArray(hierarchicalData[toolName][targetMenu])) {
                    hierarchicalData[toolName][targetMenu].push(featureName);
                }
            }
        });
        
        // Clean up any empty menus and ensure all menus have arrays
        Object.keys(hierarchicalData).forEach(toolName => {
            Object.keys(hierarchicalData[toolName]).forEach(menuName => {
                if (menuName.startsWith('_')) return; // Skip metadata
                
                if (!Array.isArray(hierarchicalData[toolName][menuName])) {
                    hierarchicalData[toolName][menuName] = [];
                }
            });
        });
        
        // console.log('Transformed hierarchical data:', hierarchicalData);
        return hierarchicalData;
    };

    // Fetch permissions data structure
    const fetchPermission = async () => {
        try {
            const response = await fetchPermissionList();

            // console.log("bhagwati", response);
            
            setDataDisplay(response.data.dataDisplay);
            setDataGroup(response.data.dataGroup);
        } catch (error) {
            handleToastOpen("error", error.message);
        }
    };

    // Event handlers
    const handlePermissionTypeChange = async (type) => {
        if (!type) {
            setPermissionType('');
            setSelectedTarget('');
            setChecksList(new Set());
            setDataListByType([]);
            return;
        }

        setPermissionType(type);
        setSelectedTarget('');
        setChecksList(new Set());
        setIsLoading(true);
        
        try {
            await fetchPermission();
            const response = await fetchTypeList(type);
            setDataListByType(response.data);
        } catch (error) {
            handleToastOpen("error", error.message);
            setDataListByType([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleTargetChange = (targetId) => {
        if (!targetId) {
            setSelectedTarget('');
            setChecksList(new Set());
            return;
        }

        setSelectedTarget(targetId);
        
        // Find the selected item to get its permissions
        const selectedItem = dataListByType.find(item => item.id == targetId);
        
        if (selectedItem && selectedItem.permission_id) {
            // Set permissions based on the selected item's permission_id
            setChecksList(new Set(selectedItem.permission_id.split(",")));
        } else {
            // Initialize empty permissions
            setChecksList(new Set());
        }
    };

  // Helper function to get tool name from permission key
  const getToolNameFromKey = (key) => {
    const display = dataDisplay[key];
    if (!display) return '';

    // Extract tool name from patterns like "tool_toolname - description"
    if (display.startsWith('tool_')) {
        const afterTool = display.substring(5); // Remove 'tool_'
        const dashIndex = afterTool.indexOf(' - ');
        return dashIndex !== -1 ? afterTool.substring(0, dashIndex) : afterTool;
    }

    // Extract from menu_ or feature_ patterns
    const match = display.match(/(menu_|feature_)([^_]+)_/);
    return match ? match[2] : '';
};

const handlePermissionChange = (permissionKey, checked) => {
    setChecksList(prevChecksList => {
        const newChecksList = new Set(prevChecksList);
        
        if (checked) {
            newChecksList.add(permissionKey);
            
            // Auto-check parent tool when menu or feature is checked
            const permissionDisplay = dataDisplay[permissionKey];
            if (permissionDisplay) {
                // Extract tool name from menu_ or feature_ permissions
                if (permissionDisplay.startsWith('menu_') || permissionDisplay.startsWith('feature_')) {
                    const toolName = getToolNameFromKey(permissionKey);
                    
                    if (toolName) {
                        // Find the tool key for this tool name
                        const toolKey = Object.keys(dataDisplay).find(key => {
                            const display = dataDisplay[key];
                            if (display && display.startsWith('tool_')) {
                                const extractedToolName = getToolNameFromKey(key);
                                return extractedToolName === toolName;
                            }
                            return false;
                        });
                        
                        if (toolKey) {
                            newChecksList.add(toolKey);
                        }
                    }
                }
            }
        } else {
            newChecksList.delete(permissionKey);
            
            // Check if we should uncheck the parent tool
            const permissionDisplay = dataDisplay[permissionKey];
            if (permissionDisplay && (permissionDisplay.startsWith('menu_') || permissionDisplay.startsWith('feature_'))) {
                const toolName = getToolNameFromKey(permissionKey);
                
                if (toolName) {
                    // Find all menu and feature items for this tool
                    const relatedItems = Object.keys(dataDisplay).filter(key => {
                        const display = dataDisplay[key];
                        if (!display) return false;
                        
                        const keyToolName = getToolNameFromKey(key);
                        return keyToolName === toolName && 
                               (display.startsWith('menu_') || display.startsWith('feature_')) &&
                               key !== permissionKey; // Exclude the current item being unchecked
                    });
                    
                    // Check if any related items are still checked
                    const hasCheckedRelatedItems = relatedItems.some(item => newChecksList.has(item));
                    
                    // If no related items are checked, uncheck the tool header
                    if (!hasCheckedRelatedItems) {
                        const toolKey = Object.keys(dataDisplay).find(key => {
                            const display = dataDisplay[key];
                            if (display && display.startsWith('tool_')) {
                                const extractedToolName = getToolNameFromKey(key);
                                return extractedToolName === toolName;
                            }
                            return false;
                        });
                        
                        if (toolKey) {
                            newChecksList.delete(toolKey);
                        }
                    }
                }
            }
        }
        
        return newChecksList;
    });
};

    // Add batch permission change handler
    const handleBatchPermissionChange = (permissionKeys, checked) => {
        setChecksList(prevChecksList => {
            const newChecksList = new Set(prevChecksList);
            permissionKeys.forEach(key => {
                if (checked) {
                    newChecksList.add(key);
                } else {
                    newChecksList.delete(key);
                }
            });
            return newChecksList;
        });
    };

    const getDataListByType = () => {
        return dataListByType.map(item => ({
            id: item.id,
            display_val: item.display_val || item.email || item.role || item.name || item.terminal_name,
            permission_id: item.permission_id
        }));
    };

    const handleSubmitPermissions = async () => {
        if (!selectedTarget) {
            handleToastOpen("error", "Please select a target first");
            return;
        }

        setIsSubmitting(true);
        setIsLoading(true);
        
        try {
            const response = await updatePermissionList(
                permissionType, 
                selectedTarget, 
                Array.from(checksList).join(',')
            );
            
            handleToastOpen("success", response.data.message || "Permissions updated successfully");
            
            // Refresh the data to show updated permissions
            if (permissionType) {
                const typeResponse = await fetchTypeList(permissionType);
                setDataListByType(typeResponse.data);
                
                // Update the selected target's permissions in the UI
                const updatedItem = typeResponse.data.find(item => item.id == selectedTarget);
                if (updatedItem && updatedItem.permission_id) {
                    setChecksList(new Set(updatedItem.permission_id.split(",")));
                }
            }
            
        } catch (error) {
            handleToastOpen("error", error.message || "Failed to update permissions");
        } finally {
            setIsSubmitting(false);
            setIsLoading(false);
        }
    };

    // Get permissions data structure - now returns hierarchical structure
    const getPermissionsData = () => {
        // console.log('dataDisplay in getPermissionsData:', dataDisplay);
        
        if (Object.keys(dataDisplay).length > 0) {
            return transformPermissionsData(dataDisplay);
        }
        
        // Return empty object if no data
        return {};
    };

    // Check if a permission is selected - now works with IDs
    const isPermissionChecked = (permissionId) => {
        return checksList.has(permissionId.toString());
    };

    return {
        // Data
        permissionsData: getPermissionsData(),
        dataListByType,
        dataDisplay,
        dataGroup,
        
        // States
        permissionType,
        selectedTarget,
        checksList,
        isLoading,
        isSubmitting,
        
        // Event handlers
        handlePermissionTypeChange,
        handleTargetChange,
        handlePermissionChange,
        handleBatchPermissionChange,
        handleSubmitPermissions,
        
        // Utility functions
        getDataListByType,
        isPermissionChecked
    };
};

export default PermissionVM;